import { useState } from "react";
import { useForm, useFormState } from "react-final-form";
import { Trans, useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import { useDispatch, useSelector } from "src/store";

import { ConfirmDialog, DropdownItem } from "@dashboard/devices/components/shared/index";
import { selectProvisionings } from "@dashboard/provisioning/store/selectors";
import { EntityManagerRow } from "@dashboard/shared/components/EntityManagerRow/EntityManagerRow";
import { useCurrentSubscriptionExpired } from "@dashboard/workspaces/api/helpers";
import { Box, DialogContentText, useTheme } from "@mui/material";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { ControlledSelectValue } from "@shared/form/SelectField/SelectField.types";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { useConfirmDiscardChanges } from "@shared/UnsavedChangesGuard/UnsavedChangesGuard";

import { useCreateProvisioningGate } from "../ProvisioningPage.utils";
import { deleteProvisioning } from "../store";
import { selectProvisioningListStatus } from "../store/selectors";
import { Provisioning } from "../types";
import { defaultProvisioning } from "../utils";

import CreateProvisioningDialog from "./CreateProvisioningDialog";
import EditProvisioningNameDialog from "./EditProvisioningNameDialog";

export const ProvisioningManagement = ({ onSelect }: { onSelect: (provisioningId: number) => void }) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const { t } = useTranslation(["pages", "common", "modals"]);

    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);

    const parentForm = useForm();
    const { values: parentFormValues } = useFormState<Partial<Provisioning>>();
    const status = useSelector(selectProvisioningListStatus);
    const provisionings = useSelector(selectProvisionings, shallowEqual);
    const createGate = useCreateProvisioningGate();
    const isExpired = useCurrentSubscriptionExpired();
    const confirmDiscardChanges = useConfirmDiscardChanges();

    const selectedProvisioningId = parentForm.getState().values?.id;
    const selectedProvisioning = provisionings.find((p) => p.id === selectedProvisioningId) || null;

    const onChange = (value: ControlledSelectValue<false> | undefined) => {
        const provisioning = provisionings.filter((item) => item.id === value)[0];

        if (!provisioning) {
            return;
        }
        parentForm.change("id", selectedProvisioningId);
        confirmDiscardChanges(() => {
            parentForm.reset(provisioning);
            onSelect(provisioning.id);
        });
    };

    const onDelete = async (provisioningId: number | undefined) => {
        if (provisioningId) {
            await dispatch(deleteProvisioning(provisioningId))
                .unwrap()
                .then(() => {
                    showSuccessToast(t("pages:provisioningPage.toast.provisioningDeletedSuccessfully"));
                    const provisioning = provisionings.filter((item) => item.id !== provisioningId)[0];
                    parentForm.reset(provisioning || defaultProvisioning);
                    if (provisioning) {
                        onSelect(provisioning.id);
                    }
                })
                .catch(({ message = t("pages:provisioningPage.toast.provisioningDeletionFailed") }) =>
                    showErrorToast(message || t("pages:provisioningPage.toast.provisioningDeletionFailed")),
                )
                .finally(() => setDeleteDialogOpen(false));
        }
    };

    return (
        <>
            <Box style={{ paddingBottom: theme.spacing(2) }}>
                <EntityManagerRow
                    selector={
                        <ControlledSelectField
                            name="id"
                            label={
                                provisionings.length
                                    ? t("pages:provisioningPage.input.id.caption1")
                                    : t("pages:provisioningPage.input.id.caption2")
                            }
                            disabled={parentForm.getState().submitting || status === "pending" || !provisionings.length}
                            options={provisionings.map((item: Provisioning) => ({
                                label: item.name,
                                value: item.id,
                            }))}
                            fullWidth
                            onChange={onChange}
                        />
                    }
                >
                    <DropdownItem
                        label={t("common:button.create")}
                        iconName="fa-solid fa-fw fa-plus"
                        disabled={parentForm.getState().submitting || createGate.disabled}
                        onClick={() => confirmDiscardChanges(() => setCreateDialogOpen(true))}
                        data-testid="provisioningPage.btn.create"
                        tooltipProps={{ title: createGate.reason }}
                    />
                    <DropdownItem
                        label={t("common:button.rename")}
                        iconName="fa-solid fa-fw fa-pencil"
                        disabled={parentForm.getState().submitting || isExpired || !provisionings.length}
                        onClick={() => setEditDialogOpen(true)}
                        tooltipProps={{ title: t("common:tooltip.editName") }}
                    />
                    <DropdownItem
                        label={t("common:button.delete")}
                        iconName="fa-solid fa-fw fa-trash"
                        danger
                        disabled={parentForm.getState().submitting || !provisionings.length}
                        onClick={() => setDeleteDialogOpen(true)}
                        data-testid="provisioningPage.btn.delete"
                    />
                </EntityManagerRow>
            </Box>
            <CreateProvisioningDialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} onCreated={onSelect} />
            {selectedProvisioning ? (
                <EditProvisioningNameDialog
                    open={editDialogOpen}
                    onClose={() => setEditDialogOpen(false)}
                    provisioning={selectedProvisioning}
                />
            ) : null}
            <ConfirmDialog
                title={t("modals:deleteProvisioning.title")}
                dangerButton
                primaryActionText={t("common:button.delete")}
                open={deleteDialogOpen}
                onConfirm={() => onDelete(parentFormValues?.id)}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogContentText>
                    <Trans
                        ns="modals"
                        i18nKey="deleteProvisioning.description"
                        values={{ name: parentForm.getState().values?.name }}
                        components={{ bold: <strong /> }}
                    />
                </DialogContentText>
            </ConfirmDialog>
        </>
    );
};
